import { FormEvent, useState } from 'react';
import type { ContactErrors, ContactFields, Interest, SubmitStatus } from '../types/contact';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const EMPTY: ContactFields = { name: '', email: '', phone: '', message: '' };

export function useContactForm(interest: Interest | '', onInterestChange: (value: Interest | '') => void) {
  const [fields, setFields] = useState<ContactFields>(EMPTY);
  const [errors, setErrors] = useState<ContactErrors>({});
  const [status, setStatus] = useState<SubmitStatus>('idle');

  const clearError = (key: keyof ContactErrors) => {
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const update = (key: keyof ContactFields, value: string) => {
    setFields((prev) => ({ ...prev, [key]: value }));
    clearError(key);
  };

  const updateInterest = (value: Interest | '') => {
    onInterestChange(value);
    clearError('interest');
  };

  const validate = (): ContactErrors => {
    const next: ContactErrors = {};
    if (!fields.name.trim()) next.name = 'Please enter your name.';
    if (!fields.email.trim()) next.email = 'Please enter your email.';
    else if (!EMAIL_RE.test(fields.email.trim())) next.email = 'That email doesn’t look right.';
    if (fields.phone.trim() && fields.phone.replace(/\D/g, '').length < 10) {
      next.phone = 'Please enter a 10-digit phone number.';
    }
    if (!interest) next.interest = 'Pick what you’re interested in.';
    if (!fields.message.trim()) next.message = 'Tell David a little about what you need.';
    return next;
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = validate();
    setErrors(next);
    const firstInvalid = Object.keys(next).find((key) => next[key as keyof ContactErrors]);
    if (firstInvalid) {
      document.getElementById(`contact-${firstInvalid}`)?.focus();
      return;
    }
    setStatus('submitting');
    try {
      // Connect to the CRM / form endpoint here.
      await new Promise((resolve) => setTimeout(resolve, 900));
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  const reset = () => {
    setFields(EMPTY);
    setErrors({});
    onInterestChange('');
    setStatus('idle');
  };

  return { fields, errors, status, update, updateInterest, submit, reset };
}
